import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MessageSquare, Pin, Lock, Plus, ArrowLeft, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Forum() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [threads, setThreads] = useState([]);
  const [showNew, setShowNew] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/forum/categories");
      setCategories(data);
      if (data[0]) setActiveCat(data[0].id);
    })();
  }, []);

  useEffect(() => {
    if (!activeCat) return;
    (async () => {
      const { data } = await api.get(`/forum/threads?category_id=${activeCat}`);
      setThreads(data);
    })();
  }, [activeCat, showNew]);

  const canPost = user && (user.role === "member" || user.role === "admin");

  return (
    <div data-testid="forum-page" className="min-h-screen bg-black text-white">
      <TopBar user={user} onLogout={logout} />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="h-px w-10 bg-sky-400" />
              <span className="font-mono text-[11px] tracking-[0.3em] text-sky-400 uppercase">Community · Forum</span>
            </div>
            <h1 className="font-display text-4xl md:text-5xl uppercase">Το Forum μας</h1>
          </div>
          <div className="flex items-center gap-2">
            {user?.role === "admin" && (
              <Link to="/admin" data-testid="forum-admin-link" className="inline-flex items-center gap-2 border border-zinc-700 text-white text-xs uppercase tracking-widest px-4 py-2 hover:border-sky-400 hover:text-sky-400">
                <ShieldCheck className="w-3.5 h-3.5" /> Admin
              </Link>
            )}
            {canPost && (
              <button onClick={() => setShowNew(true)} data-testid="forum-new-thread-btn" className="inline-flex items-center gap-2 bg-sky-400 text-black text-xs font-bold uppercase tracking-widest px-4 py-2 hover:bg-sky-300">
                <Plus className="w-3.5 h-3.5" /> New Thread
              </button>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          <aside className="lg:col-span-3">
            <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-3">Categories</div>
            <ul className="space-y-1">
              {categories.map((c) => (
                <li key={c.id}>
                  <button
                    onClick={() => setActiveCat(c.id)}
                    data-testid={`forum-cat-${c.id}`}
                    className={`w-full text-left px-4 py-3 border transition-colors ${
                      activeCat === c.id
                        ? "border-sky-400 bg-sky-400/10 text-white"
                        : "border-zinc-900 text-zinc-400 hover:border-zinc-700"
                    }`}
                  >
                    <div className="font-bold uppercase text-sm">{c.name}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">{c.description}</div>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          <main className="lg:col-span-9">
            {threads.length === 0 ? (
              <div data-testid="forum-empty" className="border border-dashed border-zinc-800 p-16 text-center">
                <MessageSquare className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
                <div className="text-zinc-500 text-sm">Κανενα thread ακομα. {canPost && "Εισαι πρωτος!"}</div>
              </div>
            ) : (
              <ul className="space-y-3">
                {threads.map((t) => (
                  <li key={t.id}>
                    <Link
                      to={`/forum/threads/${t.id}`}
                      data-testid={`forum-thread-${t.id}`}
                      className="block border border-zinc-800 hover:border-sky-400/60 bg-zinc-950/40 p-5 transition-colors group"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            {t.pinned && <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-sky-400"><Pin className="w-3 h-3" /> Pinned</span>}
                            {t.locked && <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-red-400"><Lock className="w-3 h-3" /> Locked</span>}
                          </div>
                          <h3 className="font-display text-lg uppercase group-hover:text-sky-400 transition-colors truncate mt-1">{t.title}</h3>
                          <div className="text-xs text-zinc-500 font-mono mt-1">
                            by {t.author_name} · {new Date(t.updated_at).toLocaleString()}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-display text-2xl text-white">{t.reply_count}</div>
                          <div className="text-[10px] font-mono text-zinc-500 uppercase">replies</div>
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </main>
        </div>
      </div>

      {showNew && (
        <NewThreadModal
          categoryId={activeCat}
          categories={categories}
          onClose={() => setShowNew(false)}
          onCreated={(t) => {
            setShowNew(false);
            nav(`/forum/threads/${t.id}`);
          }}
        />
      )}
    </div>
  );
}

function TopBar({ user, onLogout }) {
  return (
    <div className="border-b border-zinc-900 bg-black/80 backdrop-blur-md sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-14 flex items-center justify-between">
        <Link to="/" data-testid="forum-back-home" className="flex items-center gap-2 text-white hover:text-sky-400">
          <ArrowLeft className="w-4 h-4" />
          <span className="font-display text-sm tracking-widest uppercase">TED <span className="text-sky-400">/</span> Forum</span>
        </Link>
        <div className="text-xs font-mono text-zinc-400 flex items-center gap-3">
          <span data-testid="forum-user-name">{user?.name}</span>
          <span className="text-zinc-700">·</span>
          <span className="text-sky-400 uppercase">{user?.role}</span>
          <button onClick={onLogout} data-testid="forum-logout" className="ml-3 border border-zinc-800 px-3 py-1 hover:border-sky-400 hover:text-sky-400">Logout</button>
        </div>
      </div>
    </div>
  );
}

function NewThreadModal({ categoryId, categories, onClose, onCreated }) {
  const [cat, setCat] = useState(categoryId);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/forum/threads", { category_id: cat, title, content });
      onCreated(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="new-thread-modal" className="w-full max-w-xl bg-zinc-950 border border-zinc-800 p-8">
        <h3 className="font-display text-2xl uppercase mb-6">New thread</h3>
        <div className="space-y-4">
          <label className="block">
            <span className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase">Category</span>
            <select value={cat || ""} onChange={(e) => setCat(e.target.value)} data-testid="new-thread-category" className="mt-2 w-full bg-black border border-zinc-800 text-white px-4 py-3 focus:outline-none focus:border-sky-400">
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase">Title</span>
            <input type="text" required minLength={3} maxLength={140} value={title} onChange={(e) => setTitle(e.target.value)} data-testid="new-thread-title" className="mt-2 w-full bg-black border border-zinc-800 text-white px-4 py-3 focus:outline-none focus:border-sky-400" />
          </label>
          <label className="block">
            <span className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase">Content (markdown-lite: **bold** *italic*)</span>
            <textarea required minLength={1} rows={8} value={content} onChange={(e) => setContent(e.target.value)} data-testid="new-thread-content" className="mt-2 w-full bg-black border border-zinc-800 text-white px-4 py-3 focus:outline-none focus:border-sky-400 font-mono text-sm" />
          </label>
          {err && <div className="text-red-400 text-xs font-mono">{err}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="border border-zinc-800 text-white px-5 py-2 text-xs uppercase tracking-widest hover:border-zinc-600">Cancel</button>
            <button type="submit" disabled={busy} data-testid="new-thread-submit" className="bg-sky-400 text-black px-5 py-2 text-xs font-bold uppercase tracking-widest hover:bg-sky-300 disabled:opacity-40">{busy ? "..." : "Create"}</button>
          </div>
        </div>
      </form>
    </div>
  );
}
